import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Crown, Award, AlertTriangle, Pencil, Check, X,
  Lightbulb, Wrench, Landmark, Globe, Coins, Network, ShieldAlert,
  TrendingUp, TrendingDown, Baby, Briefcase, Rocket, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: 'overview', title: 'Overview', icon: BookOpen },
  { key: 'problem', title: 'Problem It Solves', icon: Lightbulb },
  { key: 'technology', title: 'Technology', icon: Wrench },
  { key: 'institutional_thesis', title: 'Institutional Thesis', icon: Landmark },
  { key: 'adoption', title: 'Real-World Adoption', icon: Globe },
  { key: 'tokenomics', title: 'Tokenomics', icon: Coins },
  { key: 'ecosystem', title: 'Ecosystem', icon: Network },
  { key: 'risks', title: 'Risks', icon: ShieldAlert },
  { key: 'bull_case', title: 'Bull Case', icon: TrendingUp },
  { key: 'bear_case', title: 'Bear Case', icon: TrendingDown },
  { key: 'beginner_summary', title: 'Simple Beginner Summary', icon: Baby },
  { key: 'institutional_summary', title: 'Institutional Summary', icon: Briefcase },
  { key: 'how_it_changes_world', title: 'How It Changes The World', icon: Rocket },
];

const BADGE_STYLE = {
  'Gold Analyst': 'from-[#C5A059] to-[#D4AF37] text-black',
  'Silver Researcher': 'from-gray-300 to-gray-400 text-black',
  'Bronze Learner': 'from-orange-600 to-orange-700 text-white',
  'Keep Learning': 'from-gray-700 to-gray-800 text-gray-300',
};

const Quiz = ({ crypto, getToken, isAuthenticated, login }) => {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const quiz = crypto.quiz || [];
  if (quiz.length === 0) return null;

  const submit = async () => {
    if (!isAuthenticated) { login(); return; }
    if (Object.keys(answers).length < quiz.length) return toast.error('Answer all questions first');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/research/quiz/${crypto.slug}/submit`,
        { answers: quiz.map((_, i) => answers[i]) },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setResult(res.data);
      if (res.data.completed) toast.success(`Lesson complete! Badge earned: ${res.data.badge}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-b from-[#151515] to-[#0D0D0D] border border-[#C5A059]/30 rounded-xl p-6 mt-8" data-testid="crypto-quiz">
      <div className="flex items-center gap-2 mb-1">
        <Award size={18} className="text-[#C5A059]" />
        <h2 className="text-white font-manrope font-bold text-xl">Knowledge Quiz</h2>
      </div>
      <p className="text-gray-500 text-sm mb-6">Score 3+ to complete the lesson. 5/5 earns the Gold Analyst badge.</p>

      {result ? (
        <div className="text-center py-6" data-testid="quiz-result">
          <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${BADGE_STYLE[result.badge]} px-6 py-3 rounded-full font-bold text-lg mb-4`}>
            <Award size={20} /> {result.badge}
          </div>
          <p className="text-white text-3xl font-bold font-manrope mb-1" data-testid="quiz-score">{result.score} / {result.total}</p>
          <p className="text-gray-500 text-sm mb-6">{result.completed ? 'Lesson completed — badge saved to your progress.' : 'Score 3 or more to complete this lesson. Review the sections and retry!'}</p>
          <div className="max-w-lg mx-auto text-left space-y-2 mb-6">
            {quiz.map((q, i) => (
              <div key={i} className={`text-xs px-3 py-2 rounded-lg border ${result.results[i].correct ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-400' : 'border-red-500/30 bg-red-500/5 text-red-400'}`}>
                Q{i + 1}: {result.results[i].correct ? 'Correct' : `Incorrect — answer: ${q.options[result.results[i].correct_index]}`}
              </div>
            ))}
          </div>
          <Button onClick={() => { setResult(null); setAnswers({}); }} variant="outline"
            className="border-white/20 text-gray-300" data-testid="quiz-retry-btn">
            Retake Quiz
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {quiz.map((q, i) => (
            <div key={i} data-testid={`quiz-question-${i}`}>
              <p className="text-white text-sm font-medium mb-3">{i + 1}. {q.question}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {q.options.map((opt, oi) => (
                  <button key={oi} onClick={() => setAnswers({ ...answers, [i]: oi })}
                    data-testid={`quiz-q${i}-opt${oi}`}
                    className={`text-left text-sm px-4 py-2.5 rounded-lg border transition-colors ${
                      answers[i] === oi
                        ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]'
                        : 'border-white/10 bg-black/30 text-gray-300 hover:border-white/30'
                    }`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <Button onClick={submit} disabled={submitting}
            className="w-full bg-[#C5A059] text-black hover:bg-[#D4AF37] font-semibold py-5"
            data-testid="quiz-submit-btn">
            {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            {isAuthenticated ? 'Submit Answers & Get Badge' : 'Sign In To Take The Quiz'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default function CryptoProfile() {
  const { slug } = useParams();
  const { user, isAuthenticated, login, getToken } = useAuth();
  const [crypto, setCrypto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/research/cryptos/${slug}`)
      .then(r => setCrypto(r.data))
      .catch(() => setCrypto(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const saveEdits = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/research/cryptos/${slug}`, { sections: edits },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setCrypto(res.data);
      setEdits({});
      setEditMode(false);
      toast.success('Research updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const featureThis = async () => {
    try {
      await axios.post(`${API}/research/feature/${slug}`, {},
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setCrypto({ ...crypto, featured: true });
      toast.success(`${crypto.name} is now Crypto of the Week`);
    } catch (err) {
      toast.error('Failed to feature');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]"><Navbar />
        <div className="pt-40 flex justify-center"><Loader2 size={30} className="animate-spin text-[#C5A059]" /></div>
      </div>
    );
  }
  if (!crypto) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]"><Navbar />
        <div className="pt-40 text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Crypto not found</h1>
          <Link to="/research" className="text-[#C5A059]">← Back to Research Hub</Link>
        </div>
      </div>
    );
  }

  const stats = crypto.stats || {};

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="crypto-profile-page">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <Link to="/research" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C5A059] text-sm mb-6 transition-colors" data-testid="back-to-research">
          <ArrowLeft size={15} /> Research Hub
        </Link>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white" data-testid="crypto-name">
                {crypto.name} <span className="text-[#C5A059]">{crypto.symbol}</span>
              </h1>
              {crypto.featured && (
                <span className="inline-flex items-center gap-1 bg-[#C5A059]/15 border border-[#C5A059]/40 text-[#C5A059] text-xs px-3 py-1 rounded-full font-semibold">
                  <Crown size={12} /> Crypto of the Week
                </span>
              )}
            </div>
            <p className="text-gray-400">{crypto.tagline}</p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              {!crypto.featured && (
                <Button onClick={featureThis} variant="outline" size="sm"
                  className="border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 text-xs"
                  data-testid="feature-crypto-btn">
                  <Crown size={13} className="mr-1" /> Feature
                </Button>
              )}
              {editMode ? (
                <>
                  <Button onClick={saveEdits} disabled={saving} size="sm"
                    className="bg-[#C5A059] text-black text-xs" data-testid="save-edits-btn">
                    {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <Check size={13} className="mr-1" />} Save
                  </Button>
                  <Button onClick={() => { setEditMode(false); setEdits({}); }} variant="outline" size="sm"
                    className="border-white/20 text-gray-400 text-xs" data-testid="cancel-edits-btn">
                    <X size={13} />
                  </Button>
                </>
              ) : (
                <Button onClick={() => setEditMode(true)} variant="outline" size="sm"
                  className="border-white/20 text-gray-400 hover:text-white text-xs"
                  data-testid="edit-research-btn">
                  <Pencil size={13} className="mr-1" /> Edit Research
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Stats chips */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6" data-testid="crypto-stats">
          {[['Consensus', stats.consensus], ['TPS', stats.tps], ['Avg Fee', stats.avg_fee],
            ['Launched', stats.launch_year], ['Max Supply', stats.max_supply], ['Smart Contracts', stats.smart_contracts]].map(([label, val]) => (
            <div key={label} className="bg-[#111111] border border-white/10 rounded-lg px-3 py-2 text-center">
              <p className="text-[10px] text-gray-600 uppercase">{label}</p>
              <p className="text-xs text-[#C5A059] font-semibold mt-0.5 truncate">{val || '—'}</p>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-8">
          <AlertTriangle size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-200/70">Educational content only, not financial advice. No price predictions or buy/sell recommendations.</p>
        </div>

        {/* Why It Matters — simple quick take */}
        {crypto.quick_take && (
          <div className="mb-8" data-testid="why-it-matters">
            <h2 className="text-white font-manrope font-bold text-xl mb-4">
              Why It <span className="text-[#C5A059]">Matters</span> — in plain English
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-b from-[#1a1508] to-[#111111] border border-[#C5A059]/30 rounded-xl p-5" data-testid="quick-take-problem">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb size={16} className="text-[#C5A059]" />
                  <h3 className="text-[#C5A059] font-semibold text-sm">The Problem It Solves</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{crypto.quick_take.problem_short}</p>
              </div>
              <div className="bg-gradient-to-b from-[#1a1508] to-[#111111] border border-[#C5A059]/30 rounded-xl p-5" data-testid="quick-take-why">
                <div className="flex items-center gap-2 mb-2">
                  <Landmark size={16} className="text-[#C5A059]" />
                  <h3 className="text-[#C5A059] font-semibold text-sm">Why People Believe In It</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{crypto.quick_take.why_matters}</p>
              </div>
              <div className="bg-gradient-to-b from-[#1a1508] to-[#111111] border border-[#C5A059]/30 rounded-xl p-5" data-testid="quick-take-future">
                <div className="flex items-center gap-2 mb-2">
                  <Rocket size={16} className="text-[#C5A059]" />
                  <h3 className="text-[#C5A059] font-semibold text-sm">The Future</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{crypto.quick_take.future_outlook}</p>
              </div>
            </div>
          </div>
        )}

        {/* Sections */}
        <div className="space-y-5">
          {SECTIONS.map(({ key, title, icon: Icon }) => (
            <div key={key} className="bg-[#111111] border border-white/10 rounded-xl p-6" data-testid={`section-${key}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#C5A059]/10 flex items-center justify-center">
                  <Icon size={15} className="text-[#C5A059]" />
                </div>
                <h2 className="text-white font-manrope font-semibold text-lg">{title}</h2>
              </div>
              {editMode ? (
                <textarea
                  value={edits[key] !== undefined ? edits[key] : (crypto.sections?.[key] || '')}
                  onChange={e => setEdits({ ...edits, [key]: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 rounded-lg p-3 text-gray-300 text-sm min-h-[140px] focus:border-[#C5A059] outline-none"
                  data-testid={`edit-section-${key}`}
                />
              ) : (
                <p className="text-gray-400 text-sm leading-relaxed whitespace-pre-wrap">
                  {crypto.sections?.[key] || 'Content coming soon.'}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Quiz */}
        <Quiz crypto={crypto} getToken={getToken} isAuthenticated={isAuthenticated} login={login} />

        <div className="mt-8">
          <ShareButtons title={`${crypto.name} (${crypto.symbol}) institutional research on DigitalGoldGang`} />
        </div>
      </div>
      <Footer />
    </div>
  );
}
